import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { isEmpty, isNone } from '@ember/utils';

import TiktokPost from 'later/models/tiktok-post';
import { DEFAULT_TIKTOK_VIDEO_LIMIT_PER_PAGE, OauthSocialProfileType } from 'later/utils/constants';
import { getCookie } from 'later/utils/cookie';
import { fetch, objectToQueryStringSingleArray } from 'later/utils/fetch';
import objectPromiseProxy from 'later/utils/object-promise-proxy';
import redirect from 'shared/utils/redirect';

import type LaterConfigService from './later-config';
import type { SafeString } from '@ember/template/-private/handlebars';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type AccountModel from 'later/models/account';
import type GroupModel from 'later/models/group';
import type SocialIdentityModel from 'later/models/social-identity';
import type SocialProfileModel from 'later/models/social-profile';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type ErrorsService from 'later/services/errors';
import type ConnectProfilesService from 'later/services/social/connect-profiles';
import type { Maybe, UntypedLaterModel } from 'shared/types';
import type {
  TiktokApiResponse,
  TiktokComment,
  TiktokCommentData,
  TiktokReply,
  TiktokReplyData,
  TiktokVideoData
} from 'shared/types/tiktok';

export interface TiktokPagination {
  cursor?: number;
  hasMore?: boolean;
}

export enum TiktokAuthError {
  Code = 400,
  Message = 'unverified tiktok profile'
}

const SUCCESS_CODE = 0;

/**
 * @class TiktokService
 * @extends Service
 */

export default class TiktokService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare laterConfig: LaterConfigService;
  @service('social/connect-profiles') declare connectProfiles: ConnectProfilesService;
  @service declare store: StoreService;

  @readOnly('auth.currentAccount') declare currentAccount: AccountModel;
  @readOnly('auth.currentGroup') declare currentGroup: GroupModel;

  // Default query fields
  videoQueryFields = [
    'caption',
    'comments',
    'create_time',
    'item_id',
    'likes',
    'share_url',
    'shares',
    'thumbnail_url',
    'video_views'
  ];

  // Default query limits
  videoLimit = 10;
  commentLimit = 10;
  replyLimit = 10;

  get baseProxyUrl(): string {
    return `${this.laterConfig.igProxy}/proxy/tiktok`;
  }

  async addProfile(name: string, socialIdentity: SocialIdentityModel, group: GroupModel): Promise<void> {
    const socialProfileAttributes = {
      group,
      nickname: name,
      account: this.currentAccount,
      socialIdentity,
      profileType: 'tiktok'
    };

    if (socialIdentity?.id) {
      socialProfileAttributes.socialIdentity = socialIdentity;
    }

    const socialProfile = this.store.createRecord('social-profile', socialProfileAttributes);
    try {
      const savedProfile = await socialProfile.save();
      //reqd to get new social identity for the saved profile
      savedProfile.get('socialIdentity');
      this.alerts.success(this.intl.t('alerts.tiktok.add_profile.success', { name: savedProfile.name }));
    } catch (adapterError) {
      socialProfile.rollbackAttributes();
      this.errors.handleAdapter(adapterError, socialProfile);
    }
  }

  /**
   *
   * @method refresh
   * @param {SocialProfile} socialProfile The Tiktok Social Profile to be refreshed
   */
  refresh(socialProfile: SocialProfileModel, redirectPath: string): void {
    if (!socialProfile.canConnectTiktokApi) {
      return; //safeguard
    }

    this.loginWithBusinessApiAccount(socialProfile, redirectPath);
  }

  /**
   * Redirects the user to the Tiktok Business API login page
   *
   * @method loginWithBusinessApiAccount
   * @param {SocialProfile} socialProfile The Tiktok Social Profile to be refreshed
   */
  loginWithBusinessApiAccount(socialProfile: SocialProfileModel, redirectPath: string): void {
    const groupId = socialProfile ? socialProfile.get('group').get('id') : this.currentGroup.id;
    const refreshSocialProfile = socialProfile ? `refresh_social_profile_id=${socialProfile.id}` : '';
    const path = this.connectProfiles.oAuthPath({
      socialProfileType: OauthSocialProfileType.TiktokBusiness,
      redirectPath
    });

    if (!this.auth.currentAccount.canConnectTiktokApi) {
      this.errors.log(new Error('Not authorized to connect to Tiktok Business'), {
        socialProfileId: socialProfile.get('id')
      });

      return; // safeguard
    }

    if (isNone(this.currentGroup)) {
      redirect(`${path}&${refreshSocialProfile}`);
    } else {
      redirect(`${path}&group_id=${groupId}&${refreshSocialProfile}`);
    }

    redirect(`${path}&${refreshSocialProfile}`);
  }

  /**
   * Creates an OAuth window to log in to Tiktok
   *
   * @method createTiktokWithSet
   */
  createTiktokWithSet(setId: string, group: GroupModel, redirectPath: string): void {
    const path = this.connectProfiles.oAuthPath({
      socialProfileType: OauthSocialProfileType.TiktokBusiness,
      redirectGroupSlug: group.slug,
      redirectPath
    });

    if (isNone(setId)) {
      redirect(`${path}&group_id=${group.id}`);
    } else {
      redirect(`${path}&social_identity_id=${setId}`);
    }
  }

  /**
   * Fetches Recent Media (Posts) for specified socialProfile,
   * can handle paging through this endpoint.
   *
   * @method fetchRecentMedia
   * @param {SocialProfile} socialProfileProxy The socialProfile making the request (can be an ObjectProxy)
   * @param {Object} [pagination] Pagination object return from a
   * previous call to this method. Used to get the next page of data
   *
   * @return {Promise|Array<TiktokPost>} Formatted Recent TikTok Posts
   */
  async fetchRecentMedia(
    socialProfileProxy: Maybe<SocialProfileModel>,
    pagination: TiktokPagination,
    videoLimitPerPage: number = DEFAULT_TIKTOK_VIDEO_LIMIT_PER_PAGE
  ): Promise<{ posts: UntypedLaterModel<'TiktokPost'>[] | undefined; pagination: TiktokPagination | undefined }> {
    const socialProfile = await objectPromiseProxy(socialProfileProxy);

    // Avoid calling API if profile needs refresh (expired)
    if (socialProfile.needsRefresh) {
      const error = new Error(TiktokAuthError.Message) as Error & { code: number };
      error.code = TiktokAuthError.Code;
      throw error;
    }

    const cursorSuffix = pagination && this.hasMorePages(pagination) ? `?cursor=${pagination.cursor}` : '';
    const limitSuffix = isEmpty(cursorSuffix) ? `?limit=${videoLimitPerPage}` : `&limit=${videoLimitPerPage}`;
    const urlSuffix = cursorSuffix + limitSuffix;
    const url = `/api/v2/social_profiles/${socialProfile.id}/tiktok_video_list${urlSuffix}`;

    const response = await fetch(url);
    const { video_list, cursor, has_more, error_code, error_msg } = response.data;
    const { extra } = response;

    if (error_code || error_msg || extra.error_detail) {
      throw new Error(this.intl.t('tiktok.errors.api.video_list', { error: error_msg }));
    }

    if (!video_list) {
      return { posts: undefined, pagination: undefined };
    }

    const posts = video_list.map(
      (rawPost: UntypedLaterModel<'TiktokPost'>) => new TiktokPost({ ...rawPost, socialProfile })
    );

    return { posts, pagination: { cursor, hasMore: has_more } };
  }

  /**
   * Checks whether the provided pagination object indicates
   * that there is another page of data that can be requested
   *
   * @method hasMorePages
   * @param {Object | undefined} pagination The pagination object returned
   * from a previous call to an Instagram API
   *
   * @return {Boolean}
   */
  hasMorePages(pagination: TiktokPagination | undefined): boolean {
    return Boolean(pagination?.cursor && pagination?.hasMore);
  }

  /**
   * Display alert with error message
   *
   * @method raiseError
   * @param {Array} args All arguments passed to this method
   */
  raiseError(error: SafeString | string): void {
    if (error) {
      this.alerts.warning(error);
    }
  }

  /**
   * When a Tiktok user clicks on an ad and lands on our app, Tiktok adds
   * a tiktok click id to the users cookie, which can be accessed with the key `ttclid`.
   * This function compares the tiktok click id from the URL with the tiktok click id from the server.
   * If they differ, it sets the tiktokClickId property on the user to the new click id.
   */
  updateTiktokClickId(): void {
    try {
      const currentUser = this.auth.currentUserModel;
      const cookieTiktokClickId = getCookie('ttclid');
      const serverTiktokClickId = currentUser.tiktokClickId;
      if (cookieTiktokClickId && cookieTiktokClickId !== serverTiktokClickId) {
        currentUser.set('tiktokClickId', cookieTiktokClickId);
        currentUser.save();
      }
    } catch (error) {
      this.errors.log(error);
    }
  }

  /**
   * Get videos for a Tiktok business account
   * @param businessId Tiktok business ID
   * @param businessToken Tiktok business token
   * @param params Parameters to pass in body of request
   * @returns Response from TikTok
   */
  async getVideos(businessId: string, businessToken: string, params?: ListVideosParams): Promise<TiktokVideoData> {
    const endpoint = 'business/video/list/';

    const response = await this.getTiktok<TiktokVideoData>(endpoint, businessId, businessToken, {
      fields: this.videoQueryFields,
      max_count: this.videoLimit,
      ...params
    });

    return response.data;
  }

  /**
   * Get comments on a video by ID
   * @param businessId Tiktok business ID
   * @param businessToken Tiktok business token
   * @param videoId Tiktok video ID
   * @param params Parameters to pass in body of request
   * @returns Response from TikTok
   */
  async getCommentsByVideoId(
    businessId: string,
    businessToken: string,
    videoId: string,
    params?: ListCommentsParams
  ): Promise<TiktokCommentData> {
    const endpoint = `business/comment/list/`;

    const response = await this.getTiktok<TiktokCommentData>(endpoint, businessId, businessToken, {
      video_id: videoId,
      max_count: this.commentLimit,
      ...params
    });

    return response.data;
  }

  /**
   * Get replies for a comment on a video by ID
   * @param businessId Tiktok business ID
   * @param businessToken Tiktok business token
   * @param videoId Tiktok video ID
   * @param commentId Tiktok comment ID
   * @param params Parameters to pass in body of request
   * @returns Response from TikTok
   */
  async getRepliesByCommentId(
    businessId: string,
    businessToken: string,
    videoId: string,
    commentId: string,
    params?: ListCommentsParams
  ): Promise<TiktokReplyData> {
    const endpoint = `business/comment/reply/list/`;

    const response = await this.getTiktok<TiktokReplyData>(endpoint, businessId, businessToken, {
      video_id: videoId,
      comment_id: commentId,
      max_count: this.replyLimit,
      ...params
    });

    return response.data;
  }

  /**
   * Create a new comment on a TikTok video.
   * @param businessId TikTok business ID
   * @param businessToken TikTok business token
   * @param videoId TikTok video ID
   * @param text Comment text
   * @returns Created Comment
   */
  async createComment(
    businessId: string,
    businessToken: string,
    videoId: string,
    text: string
  ): Promise<TiktokComment> {
    const endpoint = 'business/comment/create/';
    const response = await this.postTiktok<TiktokComment>(endpoint, businessId, businessToken, {
      video_id: videoId,
      text
    });
    return response.data;
  }

  /**
   * Create a new reply on a TikTok comment.
   * @param businessId TikTok business ID
   * @param businessToken TikTok business token
   * @param videoId TikTok video ID
   * @param commentId TikTok comment ID
   * @param text Reply text
   * @returns Created Reply
   */
  async createReply(
    businessId: string,
    businessToken: string,
    videoId: string,
    commentId: string,
    text: string
  ): Promise<TiktokReply> {
    const endpoint = 'business/comment/reply/create/';
    const response = await this.postTiktok<TiktokReply>(endpoint, businessId, businessToken, {
      video_id: videoId,
      comment_id: commentId,
      text
    });
    return response.data;
  }

  /**
   * Delete a TikTok comment.
   * @param businessId TikTok business ID
   * @param businessToken TikTok business token
   * @param commentId TikTok comment ID
   * @returns void
   */
  async deleteComment(businessId: string, businessToken: string, commentId: string): Promise<void> {
    const endpoint = 'business/comment/delete/';
    await this.postTiktok<void>(endpoint, businessId, businessToken, { comment_id: commentId });
  }

  /**
   * Make POST request to TikTok Business API, proxied through Jolteon
   * @param path Path to TikTok endpoint
   * @param businessId Tiktok business ID
   * @param businessToken Tiktok business token
   * @param params Parameters to pass in body of request
   * @returns Response from TikTok
   */
  async postTiktok<T>(
    path: string,
    businessId: string,
    businessToken: string,
    params?: Record<string, string | number | string[]>
  ): Promise<TiktokApiResponse<T>> {
    const body = { ...params, business_id: businessId, business_token: businessToken };
    const url = `${this.baseProxyUrl}/${path}`;

    const response = await fetch(url, {
      body,
      method: 'POST',
      headers: { 'Access-Token': businessToken }
    });

    if (response.code !== SUCCESS_CODE) {
      this.errors.log(`Error in Tiktok POST request: ${response.message}`, response);
    }

    return response;
  }

  /**
   * Make GET request to TikTok Business API, proxied through Jolteon
   * @param path Path to TikTok endpoint
   * @param businessId Tiktok business ID
   * @param businessToken Tiktok business token
   * @param params Parameters to pass in body of request
   * @returns Response from TikTok
   */
  async getTiktok<T>(
    path: string,
    businessId: string,
    businessToken: string,
    params?: Record<string, unknown>
  ): Promise<TiktokApiResponse<T>> {
    const body = { ...params, business_id: businessId, business_token: businessToken };
    const url = `${this.baseProxyUrl}/${path}${objectToQueryStringSingleArray(body)}`;

    const response = await fetch(url, {
      headers: { 'Access-Token': businessToken }
    });

    if (response.code !== SUCCESS_CODE) {
      this.errors.log(`Error in Tiktok GET request: ${response.message}`, response);
    }

    return response;
  }
}

type ListVideosParams = {
  cursor?: string | number;
  fields?: string[];
  filters?: {
    video_ids: string[];
  };
  max_count?: number;
};

type ListCommentsParams = {
  cursor?: string | number;
  max_count?: number;
  sort_field?: string;
  sort_order?: string;
  status?: string;
};

declare module '@ember/service' {
  interface Registry {
    tiktok: TiktokService;
  }
}
